/**
 * Renderers module exports
 * 
 * This module provides renderer classes for all element types:
 * - StickyRenderer: Sticky notes with shadow and text
 * - TextRenderer: Text elements with font styling
 * - ShapeRenderer: All shape types (rectangle, circle, triangle, star, arrow, line, polygon)
 * - OrganizerRenderer: Orange section wrapper with title
 * - SectionRenderer: White section box with optional title
 * - GroupRenderer: Grouped elements container
 * - ConnectorRenderer: Connectors with arrows and text labels
 * - ImageRenderer: Image elements with loading states and error handling
 */

export { StickyRenderer } from './StickyRenderer';
export { TextRenderer } from './TextRenderer';
export { ShapeRenderer } from './ShapeRenderer';
export { OrganizerRenderer } from './OrganizerRenderer';
export { SectionRenderer } from './SectionRenderer';
export { GroupRenderer } from './GroupRenderer';
export { ConnectorRenderer } from './ConnectorRenderer';
export { ImageRenderer } from './ImageRenderer';

// Factory function to create appropriate renderer based on element type
import { EditableElement, ConnectorElement } from '../editing/types';
import { BaseRenderer } from '../core/BaseRenderer';
import { IRenderer } from '../core/types';
import { StickyRenderer } from './StickyRenderer';
import { TextRenderer } from './TextRenderer';
import { ShapeRenderer } from './ShapeRenderer';
import { OrganizerRenderer } from './OrganizerRenderer';
import { SectionRenderer } from './SectionRenderer';
import { GroupRenderer } from './GroupRenderer';
import { ConnectorRenderer } from './ConnectorRenderer';
import { ImageRenderer } from './ImageRenderer';

/**
 * Create a renderer for an element based on its type
 */
export function createRenderer(element: EditableElement): IRenderer {
  switch (element.type) {
    case 'sticky':
      return new StickyRenderer(element);
    case 'text':
      return new TextRenderer(element);
    case 'shape':
      return new ShapeRenderer(element);
    case 'organizer':
      return new OrganizerRenderer(element);
    case 'section':
      return new SectionRenderer(element);
    case 'group':
      return new GroupRenderer(element);
    case 'connector':
      return new ConnectorRenderer(element as ConnectorElement);
    case 'image':
      return new ImageRenderer(element);
    default:
      throw new Error(`Unknown element type: ${element.type}`);
  }
}
